/**
 * IncidentReportForm — shared between PublicLivePOBPage (employee badge) and
 * PublicVisitorLivePOBPage (visitor card). Submits to POST /api/public/incident.
 *
 * Props:
 *   apiBase      — REACT_APP_API_URL
 *   empHash?     — employee QR hash (one of empHash | visCardHash required)
 *   visCardHash? — visitor card QR hash
 *   gps?         — { lat, lng } captured by parent page (passed straight through)
 *   onSubmitted({ id }) — called after successful POST
 *   onBack       — called when user taps the back arrow
 *   S            — style tokens object from the parent page (theme colors)
 */
import React, { useRef, useState } from 'react';
import { ArrowLeft, Camera, X, AlertCircle, CheckCircle } from 'lucide-react';

const TYPES = [
  { value: 'INJURY',        label: 'Injury' },
  { value: 'NEAR_MISS',     label: 'Near-miss' },
  { value: 'PROPERTY',      label: 'Property' },
  { value: 'ENVIRONMENTAL', label: 'Environmental' },
  { value: 'SECURITY',      label: 'Security' },
  { value: 'OTHER',         label: 'Other' },
];

// Severity color comes from the parent S object so theming stays consistent.
const SEVERITIES = [
  { value: 'LOW',      label: 'Low',      colorKey: 'green' },
  { value: 'MEDIUM',   label: 'Medium',   colorKey: 'amber' },
  { value: 'HIGH',     label: 'High',     colorKey: 'orange' },
  { value: 'CRITICAL', label: 'Critical', colorKey: 'red' },
];

// Resize an image File to max dimension (longest side) to keep payload small.
async function resizeImage(file, maxDim = 1200, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export default function IncidentReportForm({ apiBase, empHash, visCardHash, gps, onSubmitted, onBack, S }) {
  const [description, setDescription] = useState('');
  const [incidentType, setIncidentType] = useState(null);
  const [severity, setSeverity] = useState(null);
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const canSubmit = !!incidentType && !!severity && description.trim().length > 0 && !submitting;

  async function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeImage(file, 1200);
      setPhotoDataUrl(dataUrl);
    } catch (err) {
      setError('Could not read photo.');
    }
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/public/incident`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          empHash: empHash || undefined,
          visCardHash: visCardHash || undefined,
          description: description.trim(),
          incidentType,
          severity,
          photoDataUrl: photoDataUrl || undefined,
          gps: gps || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to submit incident');
        setSubmitting(false);
        return;
      }
      setSubmittedId(data.id);
      // Notify parent after a short delay so the user sees the success state.
      setTimeout(() => { onSubmitted?.({ id: data.id }); }, 1200);
    } catch (err) {
      setError('Network error — please try again.');
      setSubmitting(false);
    }
  }

  // ── Success view ──────────────────────────────────────────────
  if (submittedId) {
    return (
      <div style={{ textAlign: 'center', padding: '28px 0' }}>
        <CheckCircle size={72} color={S.green} style={{ display: 'block', margin: '0 auto 20px' }} />
        <div style={{ fontSize: '20px', fontWeight: 700, color: S.green, marginBottom: '8px' }}>
          Incident Reported
        </div>
        <div style={{ fontSize: '13px', color: S.subtle }}>
          Reference: {submittedId}
        </div>
      </div>
    );
  }

  // ── Form view ─────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
        <button onClick={onBack} aria-label="Back"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: S.muted, padding: '4px', display: 'flex' }}>
          <ArrowLeft size={20} />
        </button>
        <div style={{ fontSize: '17px', fontWeight: 700, color: S.text }}>Report Incident</div>
      </div>

      {error && (
        <div style={{ background: S.red + '22', border: `1px solid ${S.red}44`, borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '13px', color: S.red, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Type */}
      <div style={{ fontSize: '12px', fontWeight: 600, color: S.subtle, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Type
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '18px' }}>
        {TYPES.map(t => {
          const selected = incidentType === t.value;
          return (
            <button key={t.value} onClick={() => setIncidentType(t.value)} type="button"
              style={{
                padding: '12px 10px', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                border: `1.5px solid ${selected ? S.blue : S.border}`,
                background: selected ? S.blue + '22' : S.elevated,
                color: selected ? S.blue : S.text,
              }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Severity */}
      <div style={{ fontSize: '12px', fontWeight: 600, color: S.subtle, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Severity
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '6px', marginBottom: '18px' }}>
        {SEVERITIES.map(sv => {
          const selected = severity === sv.value;
          const color = S[sv.colorKey] || S.blue;
          return (
            <button key={sv.value} onClick={() => setSeverity(sv.value)} type="button"
              style={{
                padding: '10px 4px', borderRadius: '10px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                border: `1.5px solid ${selected ? color : S.border}`,
                background: selected ? color + '22' : S.elevated,
                color: selected ? color : S.muted,
              }}>
              {sv.label}
            </button>
          );
        })}
      </div>

      {/* Description */}
      <div style={{ fontSize: '12px', fontWeight: 600, color: S.subtle, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        What happened?
      </div>
      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Describe the incident..."
        rows={4}
        style={{ width: '100%', boxSizing: 'border-box', background: S.elevated, border: `1px solid ${S.border}`, borderRadius: '10px', padding: '10px 12px', color: S.text, fontSize: '14px', fontFamily: 'inherit', outline: 'none', resize: 'vertical', marginBottom: '14px' }}
      />

      {/* Photo */}
      <div style={{ marginBottom: '20px' }}>
        {photoDataUrl ? (
          <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', border: `1px solid ${S.border}` }}>
            <img src={photoDataUrl} alt="incident" style={{ width: '100%', display: 'block', maxHeight: '240px', objectFit: 'cover' }} />
            <button onClick={() => setPhotoDataUrl(null)} type="button"
              style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff' }}>
              <X size={16} />
            </button>
          </div>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', borderRadius: '10px', border: `1px dashed ${S.border}`, background: S.elevated, color: S.subtle, fontSize: '13px', cursor: 'pointer' }}>
            <Camera size={16} />
            Add a photo (optional)
            <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handlePhotoChange} />
          </label>
        )}
      </div>

      {/* Submit */}
      <button onClick={handleSubmit} disabled={!canSubmit}
        style={{ width: '100%', padding: '16px', borderRadius: '12px', border: 'none',
          background: canSubmit ? S.red : S.elevated,
          color: canSubmit ? '#fff' : S.muted,
          fontSize: '16px', fontWeight: 700, cursor: canSubmit ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
        {submitting ? 'Submitting…' : 'Submit Incident'}
      </button>
    </div>
  );
}
